import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  FaHome, 
  FaUser, 
  FaCalendarAlt, 
  FaFileAlt, 
  FaCreditCard, 
  FaEnvelope, 
  FaQuestionCircle, 
  FaBars, 
  FaTimes, 
  FaSignOutAlt,
  FaChild,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import ParentRoutes from '../../routes/parentRoutes';

/**
 * ParentPanel component serves as a layout wrapper for all parent pages
 */
const ParentPanel = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  
  // Check authentication on component mount
  useEffect(() => {
    if (!isAuthenticated || (user && user.role !== 'parent')) {
      navigate('/login', { state: { message: 'Please log in as a parent to access this page' } });
    }
  }, [isAuthenticated, user, navigate]);
  
  /**
   * Toggle sidebar visibility (mobile)
   */
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  
  /**
   * Toggle sidebar size (desktop)
   */
  const toggleSidebarSize = () => {
    setSidebarMinimized(!sidebarMinimized);
  };
  
  /**
   * Handle user logout
   */
  const handleLogout = async () => {
    try {
      await logout();
      showToast('You have been successfully logged out', 'success');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      showToast('Failed to log out. Please try again.', 'error');
    }
  };
  
  // Navigation links configuration
  const navLinks = [
    {
      path: '/parent',
      name: 'Dashboard',
      icon: <FaHome className="mr-3 h-5 w-5" />
    },
    {
      path: '/parent/students',
      name: 'My Children',
      icon: <FaChild className="mr-3 h-5 w-5" />
    },
    {
      path: '/parent/classes',
      name: 'Classes',
      icon: <FaChalkboardTeacher className="mr-3 h-5 w-5" />
    },
    {
      path: '/parent/teachers',
      name: 'Teachers',
      icon: <FaUserGraduate className="mr-3 h-5 w-5" />
    },
    {
      path: '/parent/progress',
      name: 'Progress Reports',
      icon: <FaFileAlt className="mr-3 h-5 w-5" />
    },
    {
      path: '/parent/payments',
      name: 'Payments',
      icon: <FaCreditCard className="mr-3 h-5 w-5" />
    },
    {
      path: '/parent/messages',
      name: 'Messages',
      icon: <FaEnvelope className="mr-3 h-5 w-5" />
    },
    {
      path: '/parent/support',
      name: 'Support',
      icon: <FaQuestionCircle className="mr-3 h-5 w-5" />
    },
    {
      path: '/parent/subscription',
      name: 'Subscription',
      icon: <FaCreditCard className="mr-3 h-5 w-5" />
    }
  ];
  
  // If not authenticated yet, show loading state
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      <Helmet>
        <title>Parent Portal | Meira</title>
      </Helmet>
      
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-20 transition-opacity ease-linear duration-300 md:hidden"
          onClick={toggleSidebar}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
        </div>
      )}
      
      {/* Sidebar */}
      <div 
        className={`fixed inset-y-0 left-0 flex flex-col z-30 ${
          sidebarMinimized ? 'md:w-16' : 'md:w-64'
        } max-w-xs w-full bg-primary transform transition duration-300 ease-in-out md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between h-16 flex-shrink-0 px-4 bg-primary-dark">
          <div className="flex items-center">
            <img 
              src="https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/meira_logo_design_transparent_cj7blv.png" 
              alt="Meira logo" 
              className="h-8 w-auto" 
            />
            {!sidebarMinimized && (
              <span className="ml-2 text-white text-lg font-semibold">
                Parent Portal
              </span>
            )}
          </div>
          <div className="flex">
            <button
              onClick={toggleSidebarSize}
              className="hidden md:flex h-10 w-10 rounded-full items-center justify-center text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            >
              {sidebarMinimized ? <FaChevronRight className="h-4 w-4" /> : <FaChevronLeft className="h-4 w-4" />}
            </button>
            <button
              onClick={toggleSidebar}
              className="md:hidden h-10 w-10 rounded-full flex items-center justify-center text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            >
              <FaTimes className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        {/* Sidebar navigation */}
        <div className="flex-1 h-0 overflow-y-auto">
          <nav className="flex-1 px-2 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`group flex ${sidebarMinimized ? 'justify-center' : ''} items-center px-2 py-2 text-sm font-medium rounded-md ${
                  location.pathname === link.path 
                    ? 'bg-primary-dark text-white' 
                    : 'text-white hover:bg-primary-light hover:text-white'
                }`}
                title={sidebarMinimized ? link.name : ''}
              >
                {React.cloneElement(link.icon, { 
                  className: sidebarMinimized 
                    ? "h-5 w-5" 
                    : link.icon.props.className 
                })}
                {!sidebarMinimized && link.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top navigation */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            onClick={toggleSidebar}
            className="px-4 border-r border-gray-200 text-gray-500 md:hidden focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
          >
            <FaBars className="h-6 w-6" />
          </button>
          
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex items-center">
              <h1 className="text-xl font-semibold text-gray-800">
                {navLinks.find(link => link.path === location.pathname)?.name || 'Parent Portal'}
              </h1>
            </div>
            
            {/* User dropdown */}
            <div className="ml-4 flex items-center md:ml-6">
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="max-w-xs bg-white flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <span className="sr-only">Open user menu</span>
                  <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-white">
                    <FaUser className="h-4 w-4" />
                  </div>
                  <span className="ml-2 text-gray-700 hidden md:block">
                    {user.firstName} {user.lastName}
                  </span>
                </button>
                
                {userMenuOpen && (
                  <div 
                    className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <Link 
                      to="/parent/profile" 
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Your Profile
                    </Link>
                    <Link 
                      to="/parent/settings" 
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Settings
                    </Link>
                    <button 
                      onClick={handleLogout}
                      className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        <FaSignOutAlt className="mr-2 h-4 w-4 text-gray-500" />
                        Sign out
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none p-4 md:p-6">
          <ParentRoutes />
        </main>
      </div>
    </div>
  );
};

export default ParentPanel;
